import { PortalLanguage } from '@/copy/PortalLanguage';
import { HelpScreen } from '@/screens/HelpScreen';
import { MetricsScreen } from '@/screens/MetricsScreen';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState, type PropsWithChildren } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router';

import { pendingInvite } from '@/auth/pendingInvite';
import { ADMIN_URL, isAdminHost } from '@/lib/host';
import { SessionProvider, useSession, useSuperAdmin } from '@/auth/SessionProvider';
import type { StaffClients } from '@/data/client';
import { ApprovalsScreen } from '@/screens/ApprovalsScreen';
import { AssignmentBuilderScreen } from '@/screens/AssignmentBuilderScreen';
import { AssignmentsScreen } from '@/screens/AssignmentsScreen';
import { GroupsScreen } from '@/screens/GroupsScreen';
import { InviteFamiliesScreen } from '@/screens/InviteFamiliesScreen';
import { SettingsScreen } from '@/screens/SettingsScreen';
import { InviteAcceptScreen } from '@/screens/InviteAcceptScreen';
import { RedemptionsScreen } from '@/screens/RedemptionsScreen';
import { RosterScreen } from '@/screens/RosterScreen';
import { NotOnAClub } from '@/screens/NotOnAClub';
import { NotStaffHere } from '@/screens/NotStaffHere';
import { AskAnOwner } from '@/screens/AskAnOwner';
import { SignInScreen } from '@/screens/SignInScreen';
import { ConfirmTeamEmail } from '@/screens/ConfirmTeamEmail';
import { MissionEditorScreen } from '@/screens/MissionEditorScreen';
import { TeamScreen } from '@/screens/TeamScreen';
import { ApplicationsScreen } from '@/screens/ApplicationsScreen';
import { ClubBillingScreen } from '@/screens/ClubBillingScreen';
import { ApplyScreen } from '@/screens/ApplyScreen';
import { VillagePartnersScreen } from '@/screens/VillagePartnersScreen';
import { VillageSquareScreen } from '@/screens/VillageSquareScreen';
import { IconLibraryScreen } from '@/screens/IconLibraryScreen';
import { MissionHistoryScreen } from '@/screens/MissionHistoryScreen';
import { QuestContentScreen } from '@/screens/QuestContentScreen';
import { AdminShell } from '@/shell/AdminShell';
import { Shell } from '@/shell/Shell';

function RequireSession() {
  const { status, orgId } = useSession();
  if (status === 'loading') return null;
  if (pendingInvite.read()) return <Navigate to="/invites/accept" replace />;
  if (status === 'signedOut' || !orgId) return <Navigate to="/sign-in" replace />;
  return <Outlet />;
}

/**
 * **THE ADMIN GATE ASKS ABOUT THE PERSON, NEVER ABOUT AN ORG.**
 *
 * `RequireSession` above demands an `orgId`, which is right for every club screen and would have bounced a
 * super admin straight back to sign-in: the bootstrap list needs no account row, so such a person holds no
 * membership and has no org to be scoped to. That is the same lockout the API had this morning, one layer up
 * — the gate that admits you is not the gate that serves you (preconditions Corollary A-5).
 */
function RequireSuperAdmin() {
  const { status, session } = useSession();
  const superAdmin = useSuperAdmin();
  if (status === 'loading') return null;
  if (status === 'signedOut') return <Navigate to="/sign-in" replace />;
  /*
   * **ON ADMIN THERE IS NOWHERE TO SEND THEM, so they are told rather than moved** (§8.1). `/approvals` does
   * not exist on this host — redirecting to it would land on `NotFoundRoute`, which would send them back
   * here, which would redirect again. A refusal with a way out is the only thing that terminates.
   */
  if (!superAdmin)
    return isAdminHost() ? (
      session?.contentInvite ? (
        <ConfirmTeamEmail />
      ) : session?.contentRequest ? (
        <AskAnOwner />
      ) : (
        <NotStaffHere />
      )
    ) : (
      <Navigate to="/approvals" replace />
    );
  return <Outlet />;
}

/**
 * Where an unknown path goes. A club's staff land on approvals; **somebody who is only Kiddie Kredit staff has
 * no approvals queue to land in**, and sending them to one would be a redirect loop through a screen that
 * would refuse them.
 */
function NotFoundRoute() {
  const { status, orgId } = useSession();
  if (status === 'loading') return null;
  /*
   * **AN INVITE IN FLIGHT COMES FIRST — BEFORE THE SIGNED-OUT CHECK, not after it.** Both orderings were tried
   * and smoke:browser told them apart, which is the only thing that could: a first-time director is refused by
   * `/v1/org/me` (403 — they are not staff YET, that is what the invite is for), and a refusal leaves
   * `status: 'signedOut'`. So a signed-out check placed first sends the very person holding an invite to the
   * sign-in card instead of to their invite, and they can never join. `RequireSession` has always had this
   * order; this route now matches it rather than half-matching it.
   */
  if (pendingInvite.read()) return <Navigate to="/invites/accept" replace />;
  if (status === 'signedOut') return <Navigate to="/sign-in" replace />;
  /*
   * **CLUBS LANDS A CLUB'S STAFF ON THEIR CLUB, WHATEVER ELSE THEY ARE** (Evan, 2026-09-23). This used to
   * forward `!orgId && superAdmin` to the admin site, and that was the bug: only `/admin/...` paths forward,
   * and a super admin reaches the admin site through the Quest Jr. row — never by opening clubs.
   *
   * **The condition was never the cause, though, and that matters for the fix.** It reads `!orgId`, so it
   * could only fire for somebody with NO membership at all; a super admin who is also a club's org admin has
   * one and always went to `/approvals`. What made it fire was that the account genuinely had no membership —
   * production held no orgs at all. So removing the forward is only half: the other half is having somewhere
   * for that person to LAND.
   *
   * **`/approvals` is not that somewhere**, and this is the loop the old branch was quietly avoiding:
   * `RequireSession` demands an `orgId`, so it bounces to `/sign-in`, which for a signed-in person renders
   * this route again — `/approvals` → `/sign-in` → here → forever. The ruled refusal terminates instead, and
   * carries its own way out (it signs out).
   */
  return orgId ? <Navigate to="/approvals" replace /> : <NotOnAClub />;
}

/**
 * **A BOOKMARK KEEPS WORKING** (§8.1): `clubs.kiddiekredit.com/admin/quest` is where every link written
 * before the move points, so it sends the reader to the same screen on the admin address rather than 404ing.
 *
 * `replace`, so Back does not bounce them straight back into the redirect.
 */
function ExternalRedirect({ to }: { to: string }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}

function SignInRoute() {
  const { status } = useSession();
  // Auth0 comes back to the ORIGIN, never to the path the person left from, so an invite that needed a
  // sign-in first lands here with its token parked. Finish what they were doing.
  if (status === 'signedIn' && pendingInvite.read())
    return <Navigate to="/invites/accept" replace />;
  if (status === 'signedIn') return <NotFoundRoute />;
  if (status === 'loading') return null;
  return <SignInScreen />;
}

/** DataClient errors never retry: the code is the message. */
export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { retry: false, refetchOnWindowFocus: true, staleTime: 15_000 },
      mutations: { retry: false },
    },
  });
}

export function Providers({
  children,
  clients,
  queryClient,
}: PropsWithChildren<{ clients?: StaffClients; queryClient?: QueryClient }>) {
  const [qc] = useState(() => queryClient ?? makeQueryClient());
  return (
    <QueryClientProvider client={qc}>
      <SessionProvider clients={clients}>{children}</SessionProvider>
    </QueryClientProvider>
  );
}

/**
 * /sign-in · /invites/accept · /approvals (landing) · /roster · /redemptions · /assignments (+ /new,
 * /:id) · /groups (org admins) · /settings (org admins) · /admin/quest (Kiddie Kredit staff, dark shell);
 * unknown → /approvals, or /admin/quest for somebody who is only Kiddie Kredit staff.
 */
export function AppRoutes() {
  return isAdminHost() ? <AdminRoutes /> : <ClubRoutes />;
}

/**
 * **admin.kiddiekredit.com — Kiddie Kredit's own content admin** (§8.1).
 *
 * **No club screen is registered here at all.** Not guarded, not redirected — absent. A route that exists
 * behind a guard is a route somebody can reach when the guard is wrong, and this host's whole reason for
 * existing is that the two audiences never meet each other's screens. `/invites/accept` is deliberately not
 * here either: a club's staff invitation is a club thing and its links point at clubs.
 *
 * Anything unrecognised goes to `/quest` rather than to a not-found: there is exactly one place to be on this
 * site, so a wrong URL is a typo rather than a decision to explain.
 */
function AdminRoutes() {
  return (
    <Routes>
      <Route path="/sign-in" element={<SignInRoute />} />
      <Route element={<RequireSuperAdmin />}>
        <Route element={<AdminShell />}>
          <Route path="/quest" element={<QuestContentScreen />} />
          <Route path="/team" element={<TeamScreen />} />
          <Route path="/applications" element={<ApplicationsScreen />} />
          <Route path="/billing" element={<ClubBillingScreen />} />
          <Route path="/village" element={<VillageSquareScreen />} />
          <Route path="/help" element={<HelpScreen />} />
          <Route path="/metrics" element={<MetricsScreen />} />
          <Route path="/village/partners" element={<VillagePartnersScreen />} />
          <Route path="/quest/icons" element={<IconLibraryScreen />} />
          <Route path="/quest/:adventureId/:missionId" element={<MissionEditorScreen />} />
          <Route path="/quest/:adventureId/:missionId/quiz" element={<MissionEditorScreen />} />
          <Route path="/quest/:adventureId/:missionId/history" element={<MissionHistoryScreen />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/quest" replace />} />
    </Routes>
  );
}

/**
 * **clubs.kiddiekredit.com — as it was, with one route that has moved** (§8.1).
 *
 * `/admin/quest` is kept ONLY as a redirect to the same screen on the admin address, because every link and
 * bookmark written before the move points at it. It registers no staff screen: the redirect is the entire
 * remaining presence of the content admin on this host.
 */
function ClubRoutes() {
  // Spanish for 3.0.11: the clubs portal's language, and its English · Español switch (the admin stays English)
  return (
    <PortalLanguage>
      <ClubRouteTree />
    </PortalLanguage>
  );
}

function ClubRouteTree() {
  return (
    <Routes>
      <Route path="/sign-in" element={<SignInRoute />} />
      {/* the link a staff invite email carries; it works signed in or out, and outside the shell */}
      <Route path="/invites/accept" element={<InviteAcceptScreen />} />
      {/* O18: a club asks to join — no account, no session */}
      <Route path="/apply" element={<ApplyScreen />} />
      <Route element={<RequireSession />}>
        <Route element={<Shell />}>
          <Route path="/approvals" element={<ApprovalsScreen />} />
          <Route path="/roster" element={<RosterScreen />} />
          <Route path="/redemptions" element={<RedemptionsScreen />} />
          <Route path="/assignments" element={<AssignmentsScreen />} />
          <Route path="/assignments/new" element={<AssignmentBuilderScreen />} />
          <Route path="/assignments/:id" element={<AssignmentBuilderScreen />} />
          <Route path="/groups" element={<GroupsScreen />} />
          <Route path="/invite-families" element={<InviteFamiliesScreen />} />
          <Route path="/settings" element={<SettingsScreen />} />
        </Route>
      </Route>
      <Route path="/admin/quest" element={<ExternalRedirect to={`${ADMIN_URL}/quest`} />} />
      <Route path="*" element={<NotFoundRoute />} />
    </Routes>
  );
}

export function App() {
  return (
    <Providers>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </Providers>
  );
}
